import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, TextField } from "@mui/material";

import { GinoCreditsData } from "../api/services/ginoCredits";
import { ChatOptionIcon, SendMessageIcon } from "../assets/icons/gino-icons";

const GinoChat = ({
  placeholder,
  onSend,
  chatInputRef,
  handleOnKeyDownWithinChatInput,
  isLoading,
  ginoCredits,
  isLoadingGinoCredits,
  handleOpenCreditLeftModal,
  isChatReady,
}: {
  placeholder: string;
  onSend: (message: string) => Promise<void> | void;
  chatInputRef?: React.RefObject<HTMLInputElement>;
  handleOnKeyDownWithinChatInput?: (event: React.KeyboardEvent) => Promise<void>;
  isLoading?: boolean;
  ginoCredits?: GinoCreditsData;
  isLoadingGinoCredits: boolean;
  handleOpenCreditLeftModal: () => void;
  isChatReady?: boolean;
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const initialMessage = location.state?.message ?? "";

  const [message, setMessage] = useState("");
  const hasSentInitialMessage = useRef(false);

  const isGinoCreditsNotEnough = Boolean(ginoCredits?.balance !== undefined && ginoCredits?.balance <= 5);

  const canSendInitialMessage = isChatReady ?? true;
  useEffect(() => {
    if (!initialMessage || hasSentInitialMessage.current || !canSendInitialMessage) return;

    if (isGinoCreditsNotEnough) {
      handleOpenCreditLeftModal();
      return;
    }

    const sendInitial = async () => {
      hasSentInitialMessage.current = true;
      try {
        await onSend(initialMessage);
        setMessage("");
      } catch {
        hasSentInitialMessage.current = false;
      }
    };

    void sendInitial();
  }, [initialMessage, isGinoCreditsNotEnough, handleOpenCreditLeftModal, onSend, canSendInitialMessage]);

  const handleOnSend = async () => {
    if (isGinoCreditsNotEnough) {
      handleOpenCreditLeftModal();
    } else {
      await onSend(message);
      setMessage("");
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (isGinoCreditsNotEnough) {
      handleOpenCreditLeftModal();
    } else {
      handleOnSend();
      setMessage("");
    }
  };

  const isLoadingData = isLoading || isLoadingGinoCredits;

  return (
    <Box sx={styles.inputContainer}>
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder={placeholder}
        sx={styles.input}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        value={message}
        ref={chatInputRef}
        // disabled={isLoadingData || isGinoCreditsNotEnough}
        disabled={isLoadingData}
      />
      <Box sx={styles.buttonsContainer}>
        {/* <IconButton disabled>
          <ChatOptionIcon />
        </IconButton> */}
        <IconButton sx={styles.sendButton(Boolean(isLoadingData))} onClick={handleOnSend} disabled={isLoadingData}>
          {isLoadingData && <CircularProgress size={20} />}
          {!isLoadingData && <SendMessageIcon sx={{ color: i18n.language === "ar" ? "white" : "black" }} />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default GinoChat;

const styles = {
  inputContainer: {
    position: "relative",
    width: "100%",
  },
  input: {
    flex: 1,
    width: "100%",
    height: "100%",

    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",

    borderRadius: "12px",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    "& .MuiOutlinedInput-root": {
      padding: "0 16px !important",
      borderRadius: "12px !important",
      border: "none !important",
    },
    "& .MuiInputBase-input": {
      padding: "10px 0 !important",
      borderRadius: "12px !important",
      paddingRight: "48px !important",
    },
  },
  buttonsContainer: {
    display: "flex",
    gap: "8px",
    position: "absolute",
    right: "8px",
    bottom: "8px",
  },
  sendButton: (isLoading: boolean) => ({
    background: isLoading ? "transparent !important" : "var(--Brand-600, #008B07) !important",
    padding: "10px",
    minWidth: "unset",
  }),
};
